/**
 * The cockpit's chat API (CockpitService) as the backend of the shared
 * assistant components (@digitalisation/enlixy-comp/assistant).
 *
 * - Sessions: the What's New briefing's own sessions are left out; the
 *   context of a conversation comes from sessionContexts.
 * - Stream: the chat API's events are translated to the shared event format.
 *   Its documentation `citations` become proposals, which the cockpit renders
 *   as source cards under the answer (CockpitSources). Tool names become words
 *   ("production data", "documentation"); raw tool output is not shown.
 * - Context: machine and line conversations send their context line as
 *   `instructions` (see CockpitService streamChat for why that must never
 *   carry user text).
 */

import type {
  AssistantBackend,
  AssistantEvent,
  AssistantSession,
  AssistantStoredMessage,
} from '@digitalisation/enlixy-comp/assistant';
import i18n from '../../../i18n';
import { getValidToken } from '../../../auth/tokenManager';
import { contextText, describeContext } from '../../../hooks/useCockpitContext';
import {
  createSession,
  deleteSession,
  getMessages,
  listSessions,
  renameSession,
  streamChat,
  type CockpitSession,
  type SSEEvent,
} from '../../../services/CockpitService';
import { useSAPDataStore } from '../../../store/SAPDataStore';
import { type Citation, mergeCitations } from '../citations';
import { forgetSessionContext, rememberSessionContext, sessionContextOf } from './sessionContexts';

/** What the cockpit adds to an answer: its documentation sources (KI RAG citations). */
export interface CockpitProposal {
  kind: 'citations';
  citations: Citation[];
}

/** Title prefix of the daily What's New briefing's sessions (WhatsNewService). */
const BRIEFING_PREFIX = "[What's New]";

/**
 * The backend may store assistant message content as JSON:
 *   {"response": "...", "toolsUsed": [...]}
 * Only the response text is shown.
 */
export function extractContent(raw: string): string {
  if (!raw || !raw.startsWith('{')) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed.response === 'string') return parsed.response;
  } catch {
    /* not JSON, use as-is */
  }
  return raw;
}

/** What the agent looks up, in words: MCP tools arrive as "<server>___<tool>". */
export function toolLabel(name: string | undefined): string {
  const raw = (name ?? '').trim();
  if (/execute_query$/i.test(raw)) return i18n.t('cockpit.toolProductionData', 'production data');
  if (/documentation|knowledge/i.test(raw)) return i18n.t('cockpit.toolDocumentation', 'documentation');
  const tool = raw.includes('___') ? raw.slice(raw.lastIndexOf('___') + 3) : raw;
  return tool.replace(/[_-]+/g, ' ').trim() || raw;
}

/** The chat API's stream as events of the shared assistant. */
export async function* toAssistantEvents(
  source: AsyncIterable<SSEEvent>,
): AsyncGenerator<AssistantEvent<CockpitProposal>> {
  let sawText = false;
  for await (const ev of source) {
    switch (ev.type) {
      case 'text_delta':
        if (ev.text) sawText = true;
        yield { type: 'text_delta', text: ev.text };
        break;
      case 'reasoning_delta':
        yield { type: 'reasoning_delta', text: ev.text };
        break;
      case 'tool_call':
        yield { type: 'tool_call', text: toolLabel(ev.text) };
        break;
      case 'tool_result':
        // the preview is raw tool output (rows, JSON): only the outcome is shown
        yield { type: 'tool_result', block_type: ev.block_type === 'error' ? 'error' : undefined };
        break;
      case 'citations': {
        const citations = mergeCitations([], ev.citations);
        if (citations.length > 0) yield { type: 'proposal', proposal: { kind: 'citations', citations } };
        break;
      }
      case 'message_stop': {
        // the chat API may send the whole answer here instead of text deltas
        const full = ev.response ?? ev.text;
        if (full && !sawText) {
          sawText = true;
          yield { type: 'text_delta', text: full };
        }
        break;
      }
      case 'error':
        yield { type: 'error', error: ev.error ?? ev.text };
        break;
      default:
        // block_start, block_stop, metadata: nothing to show
        break;
    }
  }
}

/** The context line sent to the agent: machine and line conversations only. */
export function instructionsFor(contextId: string | undefined): string | undefined {
  const { machines, lines } = useSAPDataStore.getState();
  const context = describeContext(contextId, machines, lines);
  return context.scope === 'general' ? undefined : context.contextString;
}

/** The list's line under a title: what the conversation is about ("Machine: 20055"). */
function subtitleOf(contextId: string | undefined): string {
  const { machines, lines } = useSAPDataStore.getState();
  return contextText(describeContext(contextId, machines, lines), (key, fallback) =>
    i18n.t(key, fallback),
  );
}

const toSession = (s: CockpitSession): AssistantSession => {
  const contextId = sessionContextOf(s.id);
  return {
    id: s.id,
    title: s.title ?? '',
    contextId,
    subtitle: subtitleOf(contextId),
    messageCount: s.message_count ?? 0,
    updatedAt: s.updated_at || s.created_at,
  };
};

export function createCockpitBackend(): AssistantBackend<CockpitProposal> {
  return {
    async listSessions() {
      const sessions = await listSessions(await getValidToken());
      return sessions.filter((s) => !(s.title ?? '').startsWith(BRIEFING_PREFIX)).map(toSession);
    },
    async createSession(contextId) {
      // No title: the chat API names a conversation after its first question.
      const session = await createSession(await getValidToken());
      rememberSessionContext(session.id, contextId);
      return toSession(session);
    },
    async messages(sessionId) {
      const messages = await getMessages(await getValidToken(), sessionId);
      return messages.map(
        (m): AssistantStoredMessage<CockpitProposal> => ({
          id: m.id,
          role: m.role,
          content: m.role === 'assistant' ? extractContent(m.content) : m.content,
        }),
      );
    },
    async renameSession(sessionId, title) {
      await renameSession(await getValidToken(), sessionId, title);
    },
    async deleteSession(sessionId) {
      await deleteSession(await getValidToken(), sessionId);
      forgetSessionContext(sessionId);
    },
    async *stream(sessionId, message, contextId, signal) {
      const token = await getValidToken();
      yield* toAssistantEvents(
        streamChat(token, sessionId, message, signal, instructionsFor(contextId)),
      );
    },
  };
}
